import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Globe,
  Info,
  Lock,
  Mail,
  Scale,
  Search,
  Shield,
  Zap,
} from "lucide-react";

const T = {
  bg: "#060B18",
  bgAlt: "#0A1128",
  bgCard: "rgba(15,25,55,0.55)",
  border: "rgba(59,130,246,0.12)",
  borderHover: "rgba(59,130,246,0.3)",
  blue: "#2563eb",
  blueLight: "#3b82f6",
  blueBright: "#60a5fa",
  blueGlow: "rgba(37,99,235,0.25)",
  amber: "#f59e0b",
  amberGlow: "rgba(245,158,11,0.15)",
  teal: "#10b981",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  white: "#ffffff",
  heading: "#f1f5f9",
};

const font = "'Plus Jakarta Sans', system-ui, sans-serif";
const mono = "'JetBrains Mono', monospace";

const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { background: ${T.bg}; }
    @keyframes fadeUp { from { opacity:0; transform:translateY(32px); } to { opacity:1; transform:translateY(0); } }
    @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes pulse { 0%,100% { opacity:0.4; } 50% { opacity:1; } }
    @keyframes glowPulse { 0%,100% { box-shadow:0 0 20px ${T.blueGlow}; } 50% { box-shadow:0 0 40px ${T.blueGlow}, 0 0 80px rgba(37,99,235,0.1); } }
    @keyframes ringReveal { from { opacity:0; transform:scale(0.85); } to { opacity:1; transform:scale(1); } }
    @keyframes slideIn { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:translateX(0); } }
    .reveal { opacity:0; animation: fadeUp 0.7s ease forwards; }
    .reveal-d1 { animation-delay:0.1s; }
    .reveal-d2 { animation-delay:0.2s; }
    .reveal-d3 { animation-delay:0.3s; }
    .reveal-d4 { animation-delay:0.4s; }
    .reveal-d5 { animation-delay:0.5s; }

    /* Responsive overrides */
    @media (max-width: 768px) {
      .landing-nav-links { display: none !important; }
      .landing-nav-cta { font-size: 12px !important; padding: 8px 14px !important; }
      .landing-hero-input-wrap { flex-direction: column !important; }
      .landing-hero-input-wrap input { width: 100% !important; }
      .landing-hero-input-wrap button { width: 100% !important; justify-content: center !important; }
      .landing-grid-2col { grid-template-columns: 1fr !important; }
      .landing-grid-3col { grid-template-columns: 1fr !important; }
      .landing-grid-features { grid-template-columns: 1fr !important; }
      .landing-carousel-card { flex-direction: column !important; }
      .landing-carousel-visual { width: 100% !important; height: 160px !important; }
      .landing-stats-row { flex-direction: column !important; gap: 24px !important; }
      .landing-stats-item { border-right: none !important; border-bottom: 1px solid ${T.border} !important; padding: 0 0 24px 0 !important; }
      .landing-stats-item:last-of-type { border-bottom: none !important; }
      .landing-cta-buttons { flex-direction: column !important; align-items: center !important; }
      .landing-footer-cols { flex-direction: column !important; gap: 32px !important; }
      .landing-footer-links { gap: 32px !important; }
      .landing-footer-bottom { flex-direction: column !important; gap: 8px !important; text-align: center !important; }
      .landing-pricing-card-highlighted { transform: none !important; }
      .landing-section-large-pad { padding-top: 80px !important; padding-bottom: 80px !important; }
      .landing-semicircles-section { min-height: auto !important; padding: 60px 0 40px !important; }
    }
  `}</style>
);

const s = {
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 24px",
  } as CSSProperties,
  heading: {
    fontFamily: font,
    fontWeight: 900,
    color: T.heading,
    letterSpacing: "-0.03em",
    lineHeight: 1.1,
  } as CSSProperties,
  gradient: {
    background: `linear-gradient(135deg, ${T.blueLight}, ${T.blueBright}, #93c5fd)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  } as CSSProperties,
  eyebrow: {
    fontFamily: mono,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: T.blueBright,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 8,
  } as CSSProperties,
  card: {
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: 16,
    backdropFilter: "blur(12px)",
    transition: "all 0.3s ease",
  } as CSSProperties,
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: font,
    fontWeight: 700,
    fontSize: 15,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.25s ease",
    textDecoration: "none",
  } as CSSProperties,
  btnPrimary: {
    background: T.blue,
    color: T.white,
    padding: "14px 28px",
  } as CSSProperties,
  btnSecondary: {
    background: "transparent",
    color: T.blueBright,
    padding: "14px 28px",
    border: `1px solid ${T.border}`,
  } as CSSProperties,
};

function useInView(
  threshold = 0.15
): [React.RefObject<HTMLElement | null>, boolean] {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function AnnouncementBar() {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <div
      style={{
        background: `linear-gradient(90deg, ${T.amber}15, ${T.amber}08)`,
        borderBottom: `1px solid ${T.amber}30`,
        padding: "10px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        position: "relative",
        zIndex: 100,
      }}
    >
      <AlertTriangle size={14} color={T.amber} />
      <span
        style={{
          fontFamily: font,
          fontSize: 13,
          fontWeight: 600,
          color: T.amber,
        }}
      >
        Termen de conformitate: 30 de zile de la notificarea DNSC — Amenzi până
        la 500.000 lei
      </span>
      <a
        href="#hero"
        style={{
          ...s.btn,
          fontSize: 12,
          padding: "4px 12px",
          background: `${T.amber}20`,
          color: T.amber,
          borderRadius: 6,
          border: `1px solid ${T.amber}40`,
        }}
      >
        Verifică acum <ChevronRight size={12} />
      </a>
      <button
        onClick={() => setVisible(false)}
        aria-label="Închide anunțul"
        style={{
          position: "absolute",
          right: 16,
          background: "none",
          border: "none",
          color: T.amber,
          cursor: "pointer",
          opacity: 0.5,
          padding: 4,
        }}
        type="button"
      >
        ✕
      </button>
    </div>
  );
}

function Nav({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav
      style={{
        position: "sticky",
        top: 0,
        zIndex: 90,
        background: scrolled ? "rgba(6,11,24,0.88)" : "transparent",
        backdropFilter: scrolled ? "blur(20px) saturate(180%)" : "none",
        borderBottom: scrolled
          ? `1px solid ${T.border}`
          : "1px solid transparent",
        transition: "all 0.4s ease",
        padding: "0 24px",
      }}
    >
      <div
        style={{
          ...s.container,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${T.blue}, ${T.blueBright})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Shield size={18} color={T.white} />
          </div>
          <span
            style={{
              fontFamily: font,
              fontWeight: 800,
              fontSize: 18,
              color: T.white,
              letterSpacing: "-0.02em",
            }}
          >
            NIS2PASS
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          <div
            className="landing-nav-links"
            style={{ display: "flex", alignItems: "center", gap: 32 }}
          >
            {["Platformă", "Prețuri", "FAQ"].map((item) => (
              <a
                key={item}
                href={`#${item
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")}`}
                style={{
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 500,
                  color: T.textMuted,
                  textDecoration: "none",
                  transition: "color 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.color = T.white;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.color = T.textMuted;
                }}
              >
                {item}
              </a>
            ))}
          </div>
          <button
            type="button"
            className="landing-nav-cta"
            onClick={() => onNavigate("/identificare-raportare")}
            style={{
              ...s.btn,
              ...s.btnPrimary,
              fontSize: 13,
              padding: "9px 20px",
              borderRadius: 8,
            }}
          >
            Începe Evaluarea
          </button>
        </div>
      </div>
    </nav>
  );
}

function Hero({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [cui, setCui] = useState("");
  const [focused, setFocused] = useState(false);

  const handleVerify = useCallback(() => {
    if (cui.length >= 4) {
      onNavigate(`/identificare-raportare?cui=${cui}`);
    } else {
      onNavigate("/identificare-raportare");
    }
  }, [cui, onNavigate]);

  return (
    <section
      id="hero"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "90px 0 80px",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${T.blue}18, transparent 70%)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage: `radial-gradient(${T.blueLight} 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -120,
          right: "15%",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${T.blue}12, transparent 70%)`,
          filter: "blur(60px)",
        }}
      />

      <div
        style={{ ...s.container, position: "relative", textAlign: "center" }}
      >
        <div
          className="reveal"
          style={{ ...s.eyebrow, justifyContent: "center", marginBottom: 24 }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: T.teal,
              animation: "pulse 2s ease infinite",
            }}
          />
          Platformă de conformitate NIS2 pentru România
        </div>

        <h1
          className="reveal reveal-d1"
          style={{
            ...s.heading,
            fontSize: "clamp(40px, 6vw, 72px)",
            marginBottom: 24,
            maxWidth: 900,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Conformitatea NIS2 <span style={s.gradient}>simplificată</span>
          <br />
          într-un singur pas.
        </h1>

        <p
          className="reveal reveal-d2"
          style={{
            fontSize: 18,
            color: T.textMuted,
            maxWidth: 600,
            margin: "0 auto 48px",
            lineHeight: 1.65,
          }}
        >
          Introdu CUI-ul companiei și primești automat încadrarea NIS2, analiza
          de risc și formularul oficial DNSC — complet automatizat.
        </p>

        <div
          className="reveal reveal-d3"
          style={{ maxWidth: 580, margin: "0 auto 40px" }}
        >
          <div
            className="landing-hero-input-wrap"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              background: "rgba(15,25,55,0.7)",
              border: `2px solid ${focused ? T.blueLight : T.border}`,
              borderRadius: 14,
              padding: 6,
              boxShadow: focused
                ? `0 0 0 4px ${T.blue}20, 0 8px 32px ${T.blue}15`
                : `0 4px 24px rgba(0,0,0,0.3)`,
              transition: "all 0.3s ease",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              style={{
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Search
                size={20}
                color={focused ? T.blueBright : T.textDim}
                style={{ transition: "color 0.3s" }}
              />
            </div>
            <input
              type="text"
              value={cui}
              onChange={(e) =>
                setCui(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleVerify();
              }}
              placeholder="Introdu codul CUI al companiei..."
              style={{
                flex: 1,
                background: "none",
                border: "none",
                outline: "none",
                fontFamily: mono,
                fontSize: 16,
                color: T.white,
                padding: "14px 0",
                letterSpacing: "0.04em",
              }}
            />
            <button
              type="button"
              onClick={handleVerify}
              style={{
                ...s.btn,
                ...s.btnPrimary,
                borderRadius: 10,
                padding: "14px 28px",
                fontSize: 14,
                whiteSpace: "nowrap",
                boxShadow: `0 4px 16px ${T.blue}40`,
              }}
            >
              Verifică Acum <ArrowRight size={16} />
            </button>
          </div>
          <p
            style={{
              fontSize: 12,
              color: T.textDim,
              marginTop: 12,
              fontFamily: mono,
            }}
          >
            Ex: 39893490 · Evaluare gratuită · Rezultat instant
          </p>
        </div>

        <div
          className="reveal reveal-d4"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 48,
          }}
        >
          {[
            { icon: <FileText size={14} />, text: "Conform OUG 155/2024" },
            { icon: <Shield size={14} />, text: "Metodologie DNSC" },
            { icon: <Lock size={14} />, text: "217 măsuri evaluate" },
          ].map((pill) => (
            <div
              key={pill.text}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 100,
                background: "rgba(15,25,55,0.5)",
                border: `1px solid ${T.border}`,
                fontSize: 13,
                fontWeight: 500,
                color: T.textMuted,
              }}
            >
              <span style={{ color: T.blueBright }}>{pill.icon}</span>
              {pill.text}
            </div>
          ))}
        </div>

        <div
          className="reveal reveal-d5"
          style={{
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
            width: "100vw",
            marginTop: 48,
            background: "rgba(148,163,184,0.06)",
            borderTop: "1px solid rgba(148,163,184,0.1)",
            borderBottom: "1px solid rgba(148,163,184,0.1)",
            padding: "14px 24px",
          }}
        >
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Info size={16} color={T.textDim} style={{ flexShrink: 0 }} />
            <span
              style={{
                fontFamily: font,
                fontSize: 13,
                color: T.textDim,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: T.textMuted, fontWeight: 600 }}>
                Informație importantă:
              </strong>{" "}
              NIS2PASS este un instrument auxiliar care nu înlocuiește Platforma
              oficială NIS2@RO a DNSC. Platforma oficială rămâne canalul
              obligatoriu de înregistrare și comunicare cu autoritatea
              competentă.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

const NEWS_ITEMS = [
  {
    image: "📊",
    title: "Vrei să știi dacă ești în scopul NIS2?",
    desc: "Primești instant un raport preliminar cu încadrarea companiei tale. Fără înregistrare, fără complicații.",
    tag: "EVALUARE",
    tagColor: T.blueBright,
  },
  {
    image: "📄",
    title: "Te-ai împotmolit în documentația NIS2?",
    desc: "Ai aflat că ești în scop, dar nu știi ce pași să urmezi? NIS2PASS te ghidează prin toate etapele de conformitate.",
    tag: "CONFORMITATE",
    tagColor: T.teal,
  },
  {
    image: "⚖️",
    title: "Ordinele DNSC nr. 1 și 2/2025 sunt în vigoare",
    desc: "Entitățile esențiale și importante au 30 de zile pentru înregistrare și 60 de zile pentru autoevaluarea riscului.",
    tag: "ACTUALIZARE DNSC",
    tagColor: T.amber,
  },
];

function NewsCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ref, inView] = useInView(0.2);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(
      () => setActive((prev) => (prev + 1) % NEWS_ITEMS.length),
      5000
    );
    return () => clearInterval(timer);
  }, [paused]);

  const item = NEWS_ITEMS[active];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`,
        background: `linear-gradient(180deg, ${T.bg}, ${T.bgAlt} 50%, ${T.bg})`,
      }}
    >
      <div style={{ ...s.container, padding: "32px 24px" }}>
        <div
          className="landing-carousel-card"
          style={{
            ...s.card,
            overflow: "hidden",
            maxWidth: 900,
            margin: "0 auto",
            border: `1px solid ${T.border}`,
            opacity: inView ? 1 : 0,
            transform: inView ? "none" : "translateY(16px)",
            transition: "all 0.7s ease",
            display: "flex",
            flexDirection: "row" as const,
            minHeight: 0,
          }}
        >
          <div
            className="landing-carousel-visual"
            style={{
              position: "relative",
              width: 280,
              flexShrink: 0,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${T.bgAlt}, #0f1d3a 50%, ${T.bgAlt})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0.06,
                backgroundImage: `linear-gradient(${T.blueBright} 1px, transparent 1px), linear-gradient(90deg, ${T.blueBright} 1px, transparent 1px)`,
                backgroundSize: "32px 32px",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${item.tagColor}18, transparent 70%)`,
                filter: "blur(30px)",
              }}
            />
            <div
              style={{
                fontSize: 56,
                position: "relative",
                zIndex: 1,
                filter: "drop-shadow(0 0 30px rgba(59,130,246,0.3))",
              }}
            >
              {item.image}
            </div>
            <div
              style={{
                position: "absolute",
                top: 12,
                left: 14,
                fontFamily: mono,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.1em",
                color: item.tagColor,
                padding: "3px 10px",
                borderRadius: 5,
                background: `${item.tagColor}15`,
                border: `1px solid ${item.tagColor}30`,
              }}
            >
              {item.tag}
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div
              style={{
                flex: 1,
                padding: "28px 32px 20px",
                background: "rgba(10,17,40,0.6)",
              }}
            >
              <h3
                style={{
                  fontFamily: font,
                  fontWeight: 800,
                  fontSize: 20,
                  color: T.heading,
                  marginBottom: 8,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.3,
                }}
              >
                {item.title}
              </h3>
              <p style={{ fontSize: 15, color: T.textMuted, lineHeight: 1.6 }}>
                {item.desc}
              </p>
            </div>

            <div
              style={{
                padding: "12px 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: `1px solid ${T.border}`,
                background: "rgba(10,17,40,0.4)",
              }}
            >
              <button
                type="button"
                aria-label="Știrea anterioară"
                onClick={() =>
                  setActive(
                    (active - 1 + NEWS_ITEMS.length) % NEWS_ITEMS.length
                  )
                }
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: T.bgCard,
                  border: `1px solid ${T.border}`,
                  color: T.textMuted,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = T.blueLight;
                  e.currentTarget.style.color = T.white;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.color = T.textMuted;
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <div style={{ display: "flex", gap: 6 }}>
                {NEWS_ITEMS.map((_, i) => (
                  <button
                    type="button"
                    key={_.tag}
                    aria-label={`Mergi la știrea ${i + 1}`}
                    onClick={() => setActive(i)}
                    style={{
                      width: i === active ? 24 : 7,
                      height: 7,
                      borderRadius: 4,
                      border: "none",
                      cursor: "pointer",
                      background: i === active ? T.blue : `${T.textDim}40`,
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-label="Știrea următoare"
                onClick={() => setActive((active + 1) % NEWS_ITEMS.length)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: T.bgCard,
                  border: `1px solid ${T.border}`,
                  color: T.textMuted,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = T.blueLight;
                  e.currentTarget.style.color = T.white;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = T.border;
                  e.currentTarget.style.color = T.textMuted;
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PlatformCapabilities() {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      id="platforma"
      className="landing-section-large-pad"
      style={{ padding: "140px 0", position: "relative" }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 60% 40% at 20% 50%, ${T.blue}06, transparent)`,
        }}
      />
      <div style={{ ...s.container, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div style={{ ...s.eyebrow, justifyContent: "center" }}>
            <div style={{ width: 20, height: 1, background: T.blueBright }} />
            Ce face NIS2PASS
          </div>
          <h2
            style={{
              ...s.heading,
              fontSize: "clamp(36px, 4.5vw, 52px)",
              marginBottom: 16,
            }}
          >
            NIS2PASS nu înlocuiește <span style={s.gradient}>NIS2@RO</span> — o
            completează.
          </h2>
          <p
            style={{
              fontSize: 17,
              color: T.textMuted,
              maxWidth: 600,
              margin: "0 auto",
            }}
          >
            Platforma statului rămâne canalul oficial. NIS2PASS automatizează
            pregătirea documentelor într-un format conform cerințelor legale.
          </p>
        </div>

        <div
          className="landing-grid-2col"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 32,
            alignItems: "start",
          }}
        >
          <div
            style={{
              ...s.card,
              padding: 40,
              borderRadius: 20,
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(32px)",
              transition: "all 0.7s ease",
              background: "rgba(15,25,55,0.35)",
              border: `1px solid ${T.border}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 28,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: `${T.teal}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={20} color={T.teal} />
              </div>
              <h3
                style={{
                  fontFamily: font,
                  fontWeight: 800,
                  fontSize: 20,
                  color: T.heading,
                }}
              >
                Platforma NIS2PASS facilitează:
              </h3>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {[
                "Verificarea preliminară a posibilei încadrări în domeniul NIS2",
                "Ghidarea pas cu pas în parcurgerea etapelor de conformitate",
                "Completarea corectă a documentelor solicitate de DNSC",
                "Generarea formularelor în formatul oficial cerut",
              ].map((text) => (
                <div
                  key={text}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: `${T.teal}15`,
                      border: `1.5px solid ${T.teal}40`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 1,
                    }}
                  >
                    <Check size={13} color={T.teal} strokeWidth={3} />
                  </div>
                  <span
                    style={{
                      fontSize: 16,
                      color: T.text,
                      lineHeight: 1.55,
                    }}
                  >
                    {text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(32px)",
              transition: "all 0.7s ease 0.15s",
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            <div
              className="landing-grid-features"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              {[
                {
                  icon: <Search size={20} />,
                  title: "Preluare automată date",
                  desc: "Date entități din bazele de date publice pe baza CUI-ului",
                },
                {
                  icon: <Zap size={20} />,
                  title: "Evaluare inteligentă",
                  desc: "Algoritmi AI conform metodologiei oficiale DNSC",
                },
                {
                  icon: <FileText size={20} />,
                  title: "Formular oficial",
                  desc: "Generare automată în formatul cerut de DNSC",
                },
                {
                  icon: <BookOpen size={20} />,
                  title: "Ghid complet",
                  desc: "Parcurgere pas cu pas a cerințelor de conformitate",
                },
              ].map((feat) => (
                <div
                  key={feat.title}
                  style={{
                    ...s.card,
                    padding: 20,
                    borderRadius: 14,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = T.borderHover;
                    e.currentTarget.style.background = `${T.blue}08`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = T.border;
                    e.currentTarget.style.background = T.bgCard;
                  }}
                >
                  <div style={{ color: T.blueBright }}>{feat.icon}</div>
                  <h4
                    style={{
                      fontFamily: font,
                      fontWeight: 700,
                      fontSize: 14,
                      color: T.heading,
                    }}
                  >
                    {feat.title}
                  </h4>
                  <p
                    style={{
                      fontSize: 13,
                      color: T.textDim,
                      lineHeight: 1.5,
                    }}
                  >
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>

            <div
              style={{
                borderRadius: 16,
                padding: 24,
                overflow: "hidden",
                background: `linear-gradient(135deg, ${T.blue}, #1d4ed8)`,
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.08,
                  backgroundImage: `radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)`,
                  backgroundSize: "20px 20px",
                }}
              />
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <ExternalLink size={18} color={T.white} />
                  <h3
                    style={{
                      fontFamily: font,
                      fontWeight: 800,
                      fontSize: 17,
                      color: T.white,
                    }}
                  >
                    Resurse oficiale DNSC
                  </h3>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {[
                    {
                      text: "Înregistrare entități DNSC",
                      href: "https://nis2.dnsc.ro",
                    },
                    {
                      text: "Obligațiile entităților înregistrate",
                      href: "https://dnsc.ro",
                    },
                  ].map((link) => (
                    <a
                      key={link.text}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 15,
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.9)",
                        textDecoration: "none",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.color = T.white;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.color =
                          "rgba(255,255,255,0.9)";
                      }}
                    >
                      <ArrowRight size={15} /> {link.text}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const LAYERS = [
  {
    title: "COMPANIA",
    items: [
      "Sectoare de activitate",
      "Tipuri de activități",
      "Dimensiune companie",
    ],
    color: "#3b82f6",
  },
  {
    title: "IDENTIFICARE NIS2",
    items: ["Important", "Esențial", "Art. 9 (Efect perturbator)"],
    color: "#2563eb",
  },
  {
    title: "DECIZIE DE IDENTIFICARE",
    items: ["Sectoare", "Nr. decizie"],
    color: "#1d4ed8",
  },
  {
    title: "ANALIZĂ DE RISC",
    items: [
      "Valori implicite",
      "Impact & probabilitate",
      "Calcul nivel risc",
      "Încadrare nivel",
    ],
    color: "#1e40af",
  },
  {
    title: "EVALUARE MATURITATE",
    items: [
      "CyFun®",
      "Controale securitate",
      "Niveluri maturitate",
      "Plan remediere",
    ],
    color: "#1e3a8a",
  },
  {
    title: "IMPLEMENTARE CYFUN",
    items: ["Politici", "Proceduri", "Procese", "Utilitare"],
    color: "#60a5fa",
  },
  {
    title: "AUDIT ȘI CONFORMITATE",
    items: ["CISO", "Training", "MFA", "SIEM", "SOC", "Vulnerability Scanning"],
    color: "#93c5fd",
  },
];

function SemicirclesSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredLayer, setHoveredLayer] = useState<number | null>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const baseRadius = 110;
  const ringWidth = 68;
  const gap = 6;
  const maxR = baseRadius + LAYERS.length * (ringWidth + gap) + 30;
  const svgW = (maxR + 60) * 2;
  const svgH = maxR + 90;
  const cx = svgW / 2;
  const cy = svgH - 15;

  return (
    <section
      ref={sectionRef}
      className="landing-semicircles-section"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "140px 0 60px",
        background: `linear-gradient(180deg, ${T.bg}, ${T.bgAlt} 30%, ${T.bgAlt} 70%, ${T.bg})`,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.025,
          backgroundImage: `linear-gradient(${T.blueLight}40 1px, transparent 1px), linear-gradient(90deg, ${T.blueLight}40 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
        }}
      />

      <div
        style={{
          position: "relative",
          maxWidth: 1400,
          margin: "0 auto",
          padding: "0 24px",
          width: "100%",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ ...s.eyebrow, justifyContent: "center" }}>
            <div style={{ width: 20, height: 1, background: T.blueBright }} />
            Platforma NIS2PASS
          </div>
          <h2
            style={{
              ...s.heading,
              fontSize: "clamp(36px, 4.5vw, 52px)",
              marginBottom: 16,
            }}
          >
            Acoperă <span style={s.gradient}>întreg ciclul</span> de
            conformitate.
          </h2>
          <p
            style={{
              fontSize: 17,
              color: T.textMuted,
              maxWidth: 600,
              margin: "0 auto",
            }}
          >
            De la identificarea companiei până la audit — 7 niveluri integrate
            într-o singură platformă.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            style={{ width: "100%", maxWidth: 1300, height: "auto" }}
          >
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="5" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <radialGradient id="coreG" cx="50%" cy="100%" r="50%">
                <stop offset="0%" stopColor={T.blueBright} />
                <stop offset="100%" stopColor={T.blue} />
              </radialGradient>
            </defs>

            {LAYERS.map((layer, i) => {
              const rInner = baseRadius + i * (ringWidth + gap);
              const rOuter = rInner + ringWidth;
              const isHovered = hoveredLayer === i;

              return (
                <g
                  key={layer.title}
                  onMouseEnter={() => setHoveredLayer(i)}
                  onMouseLeave={() => setHoveredLayer(null)}
                  style={{
                    cursor: "pointer",
                    opacity: isVisible
                      ? hoveredLayer !== null && !isHovered
                        ? 0.4
                        : 1
                      : 0,
                    transition: "opacity 0.4s ease",
                    transform: isVisible ? "scale(1)" : "scale(0.9)",
                    transformOrigin: `${cx}px ${cy}px`,
                    animation: isVisible
                      ? `ringReveal 0.6s ease ${i * 0.08}s both`
                      : "none",
                  }}
                >
                  <path
                    d={`M ${cx - rOuter} ${cy} A ${rOuter} ${rOuter} 0 0 1 ${cx + rOuter} ${cy} L ${cx + rInner} ${cy} A ${rInner} ${rInner} 0 0 0 ${cx - rInner} ${cy} Z`}
                    fill={isHovered ? `${layer.color}30` : `${layer.color}10`}
                    stroke={isHovered ? `${layer.color}` : `${layer.color}30`}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    style={{ transition: "all 0.3s ease" }}
                  />
                  <path
                    id={`title-${i}`}
                    d={`M ${cx - (rOuter - 22)} ${cy} A ${rOuter - 22} ${rOuter - 22} 0 0 1 ${cx + (rOuter - 22)} ${cy}`}
                    fill="none"
                  />
                  <text style={{ pointerEvents: "none" }}>
                    <textPath
                      href={`#title-${i}`}
                      startOffset="50%"
                      textAnchor="middle"
                      style={{
                        fontFamily: mono,
                        fontSize: Math.max(11, 15 - i * 0.4),
                        fontWeight: 700,
                        fill: isHovered ? T.white : layer.color,
                        letterSpacing: "0.1em",
                        transition: "fill 0.3s",
                      }}
                    >
                      {layer.title}
                    </textPath>
                  </text>
                  <path
                    id={`items-${i}`}
                    d={`M ${cx - (rInner + ringWidth * 0.38)} ${cy} A ${rInner + ringWidth * 0.38} ${rInner + ringWidth * 0.38} 0 0 1 ${cx + (rInner + ringWidth * 0.38)} ${cy}`}
                    fill="none"
                  />
                  <text style={{ pointerEvents: "none" }}>
                    <textPath
                      href={`#items-${i}`}
                      startOffset="50%"
                      textAnchor="middle"
                      style={{
                        fontFamily: font,
                        fontSize: Math.max(10, 13 - i * 0.3),
                        fontWeight: 500,
                        fill: isHovered ? T.text : T.textDim,
                        transition: "fill 0.3s",
                      }}
                    >
                      {layer.items.join("  ·  ")}
                    </textPath>
                  </text>
                </g>
              );
            })}

            <circle
              cx={cx}
              cy={cy}
              r={baseRadius - 5}
              fill="url(#coreG)"
              opacity={isVisible ? 1 : 0}
              filter="url(#glow)"
              style={{ transition: "opacity 0.6s ease" }}
            />
            <text
              x={cx}
              y={cy - 24}
              textAnchor="middle"
              style={{
                fontFamily: font,
                fontSize: 22,
                fontWeight: 900,
                fill: T.white,
                letterSpacing: "-0.02em",
              }}
            >
              NIS2PASS
            </text>
            <text
              x={cx}
              y={cy - 4}
              textAnchor="middle"
              style={{
                fontFamily: font,
                fontSize: 11,
                fontWeight: 500,
                fill: "rgba(255,255,255,0.7)",
                letterSpacing: "0.08em",
              }}
            >
              PLATFORMĂ UNIFICATĂ
            </text>
          </svg>
        </div>

        <div style={{ textAlign: "center", minHeight: 70, marginTop: 24 }}>
          {hoveredLayer !== null ? (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <h3
                style={{
                  fontFamily: font,
                  fontWeight: 800,
                  fontSize: 24,
                  color: T.heading,
                  marginBottom: 8,
                }}
              >
                {LAYERS[hoveredLayer].title}
              </h3>
              <p style={{ fontSize: 16, color: T.textMuted }}>
                {LAYERS[hoveredLayer].items.join("  ·  ")}
              </p>
            </div>
          ) : (
            <p
              style={{
                fontSize: 15,
                color: T.textDim,
                fontStyle: "italic",
              }}
            >
              Explorează fiecare nivel — treci cursorul peste diagrama de mai
              sus
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function LegalFramework() {
  const [ref, inView] = useInView();
  const laws = [
    {
      tag: "DIRECTIVA UE",
      tagColor: T.blueBright,
      icon: <Globe size={24} />,
      title: "Directiva (UE) 2022/2555",
      sub: "NIS2 — Cadrul european pentru securitate cibernetică",
      desc: "Directiva europeană privind securitatea cibernetică care stabilește standarde minime pentru organizații din sectoare critice.",
    },
    {
      tag: "TRANSPUNERE",
      tagColor: T.amber,
      icon: <FileText size={24} />,
      title: "OUG nr. 155/2024",
      sub: "Transpunere națională",
      desc: "Ordonanța de Urgență a Guvernului nr. 155/2024 transpune NIS2 în legislația română, stabilind cadrul legal național.",
    },
    {
      tag: "LEGE",
      tagColor: T.teal,
      icon: <Scale size={24} />,
      title: "Legea nr. 124/2025",
      sub: "Aprobare și completare",
      desc: "Legea nr. 124/2025 aprobă și completează OUG 155/2024, finalizând transpunerea directivei în legislația națională.",
    },
  ];

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="landing-section-large-pad"
      style={{
        padding: "140px 0",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(180deg, ${T.bg}, ${T.bgAlt} 40%, ${T.bgAlt} 60%, ${T.bg})`,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage: `linear-gradient(${T.blueBright} 1px, transparent 1px), linear-gradient(90deg, ${T.blueBright} 1px, transparent 1px)`,
          backgroundSize: "80px 80px",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 800,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${T.blue}08, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />

      <div style={{ ...s.container, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <div style={{ ...s.eyebrow, justifyContent: "center" }}>
            <div style={{ width: 20, height: 1, background: T.amber }} />
            Cadrul Legal
          </div>
          <h2
            style={{
              ...s.heading,
              fontSize: "clamp(36px, 4.5vw, 52px)",
              marginBottom: 16,
            }}
          >
            Directiva NIS2 <span style={s.gradient}>în România</span>
          </h2>
          <p
            style={{
              fontSize: 17,
              color: T.textMuted,
              maxWidth: 650,
              margin: "0 auto",
            }}
          >
            Implementarea Directivei (UE) 2022/2555 în legislația națională prin
            OUG 155/2024 reprezintă cea mai semnificativă schimbare de paradigmă
            în guvernanța securității cibernetice din România.
          </p>
        </div>

        <div
          className="landing-grid-3col"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 24,
            marginBottom: 40,
          }}
        >
          {laws.map((law, i) => (
            <div
              key={law.title}
              style={{
                position: "relative",
                borderRadius: 20,
                overflow: "hidden",
                background: `linear-gradient(180deg, rgba(15,25,55,0.6), rgba(10,17,40,0.8))`,
                border: `1px solid ${T.border}`,
                padding: "32px 28px",
                opacity: inView ? 1 : 0,
                transform: inView ? "none" : "translateY(40px)",
                transition: `all 0.7s ease ${i * 0.12}s`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${law.tagColor}50`;
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.transform = "none";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, transparent, ${law.tagColor}, transparent)`,
                  opacity: 0.6,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${law.tagColor}10, transparent)`,
                  filter: "blur(30px)",
                }}
              />

              <div style={{ position: "relative" }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: `${law.tagColor}12`,
                    border: `1px solid ${law.tagColor}25`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: law.tagColor,
                    marginBottom: 20,
                  }}
                >
                  {law.icon}
                </div>

                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    color: law.tagColor,
                  }}
                >
                  {law.tag}
                </span>
                <h3
                  style={{
                    fontFamily: font,
                    fontWeight: 800,
                    fontSize: 22,
                    color: T.heading,
                    marginTop: 8,
                    marginBottom: 6,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {law.title}
                </h3>
                <p
                  style={{
                    fontSize: 13,
                    color: law.tagColor,
                    fontWeight: 600,
                    marginBottom: 12,
                  }}
                >
                  {law.sub}
                </p>
                <p
                  style={{
                    fontSize: 14,
                    color: T.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {law.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            borderRadius: 16,
            padding: "24px 32px",
            overflow: "hidden",
            background: `linear-gradient(135deg, ${T.amber}08, ${T.amber}04)`,
            border: `1px solid ${T.amber}25`,
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            opacity: inView ? 1 : 0,
            transition: "all 0.7s ease 0.4s",
          }}
        >
          <AlertTriangle
            size={22}
            color={T.amber}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <div>
            <h4
              style={{
                fontFamily: font,
                fontWeight: 800,
                fontSize: 16,
                color: T.amber,
                marginBottom: 6,
              }}
            >
              Obligația de conformare
            </h4>
            <p
              style={{
                fontSize: 14,
                color: T.textMuted,
                lineHeight: 1.7,
              }}
            >
              Obligația de conformare nu este un eveniment singular, ci un{" "}
              <strong style={{ color: T.text }}>proces continuu</strong> care
              începe cu notificarea în Registrul Entităților Esențiale și
              Importante și evoluează către managementul riscului și raportarea
              incidentelor. Cadrul legal impune{" "}
              <strong style={{ color: T.text }}>termene stricte</strong>: 30 de
              zile pentru notificare, 60 de zile pentru autoevaluarea riscului.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SocialProof() {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      style={{
        padding: "60px 0",
        borderTop: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <div
        className="landing-stats-row"
        style={{
          ...s.container,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 48,
          flexWrap: "wrap",
          opacity: inView ? 1 : 0,
          transition: "opacity 0.7s ease",
        }}
      >
        {[
          { num: "350+", label: "Evaluări completate" },
          { num: "98%", label: "Formulare acceptate DNSC" },
          { num: "50+", label: "Companii active" },
        ].map((item, i) => (
          <div
            key={item.label}
            className="landing-stats-item"
            style={{
              textAlign: "center" as const,
              padding: "0 32px",
              borderRight: i < 2 ? `1px solid ${T.border}` : "none",
            }}
          >
            <div
              style={{
                fontFamily: font,
                fontWeight: 900,
                fontSize: 28,
                color: T.blueBright,
                letterSpacing: "-0.02em",
              }}
            >
              {item.num}
            </div>
            <div style={{ fontSize: 13, color: T.textDim, marginTop: 4 }}>
              {item.label}
            </div>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 20px",
            borderRadius: 10,
            background: `${T.teal}10`,
            border: `1px solid ${T.teal}25`,
          }}
        >
          <Check size={16} color={T.teal} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.teal }}>
            Aliniat cu metodologiile DNSC
          </span>
        </div>
      </div>
    </section>
  );
}

function Pricing({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [ref, inView] = useInView();
  const tiers = [
    {
      name: "Evaluare",
      tag: "GRATUIT",
      price: "0",
      desc: "Evaluare inițială completă — fără card, fără obligații.",
      features: [
        "Verificare CUI instant",
        "Încadrare NIS2 automată",
        "Analiză de risc de bază",
        "Formular notificare DNSC",
        "1 raport de evaluare",
      ],
      cta: "Începe Gratuit",
      highlighted: false,
    },
    {
      name: "Business",
      tag: "RECOMANDAT",
      price: "Personalizat",
      desc: "Ciclu complet de conformitate cu suport prioritar.",
      features: [
        "Tot ce include Evaluare +",
        "Evaluare maturitate CyFun®",
        "Plan de remediere detaliat",
        "Rapoarte nelimitate",
        "Export format DNSC",
        "Suport email prioritar",
      ],
      cta: "Solicită Ofertă",
      highlighted: true,
    },
    {
      name: "Enterprise",
      tag: "ENTERPRISE & CONSULTANȚĂ",
      price: "Personalizat",
      desc: "Implementare completă cu CISO dedicat și audit.",
      features: [
        "Tot ce include Business +",
        "CISO specializat alocat",
        "Training management NIS2",
        "Implementare SIEM & SOC",
        "Vulnerability scanning",
        "Audit anual de conformitate",
      ],
      cta: "Contactează-ne",
      highlighted: false,
    },
  ];

  return (
    <section
      id="preturi"
      className="landing-section-large-pad"
      style={{ padding: "140px 0", position: "relative" }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 70% 50% at 50% 20%, ${T.blue}08, transparent)`,
        }}
      />
      <div style={{ ...s.container, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div style={{ ...s.eyebrow, justifyContent: "center" }}>
            <div style={{ width: 20, height: 1, background: T.blueBright }} />
            Alege planul potrivit
          </div>
          <h2
            style={{
              ...s.heading,
              fontSize: "clamp(36px, 4vw, 48px)",
              marginBottom: 16,
            }}
          >
            Prețuri <span style={s.gradient}>transparente</span>
          </h2>
          <p
            style={{
              fontSize: 17,
              color: T.textMuted,
              maxWidth: 500,
              margin: "0 auto",
            }}
          >
            Începe gratuit cu evaluarea inițială. Extinde pe măsură ce ai
            nevoie.
          </p>
        </div>

        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className="landing-grid-3col"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 24,
            alignItems: "stretch",
          }}
        >
          {tiers.map((tier, i) => (
            <div
              key={tier.name}
              className={
                tier.highlighted
                  ? "landing-pricing-card-highlighted"
                  : undefined
              }
              style={{
                ...s.card,
                padding: 36,
                transform: inView
                  ? tier.highlighted
                    ? "scale(1.04)"
                    : "none"
                  : "translateY(32px)",
                opacity: inView ? 1 : 0,
                transition: `all 0.6s ease ${i * 0.12}s`,
                border: tier.highlighted
                  ? `2px solid ${T.blue}`
                  : `1px solid ${T.border}`,
                boxShadow: tier.highlighted
                  ? `0 0 40px ${T.blue}15, 0 20px 60px rgba(0,0,0,0.3)`
                  : "none",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {tier.highlighted && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${T.blue}, ${T.blueBright})`,
                  }}
                />
              )}
              <div style={{ marginBottom: 24 }}>
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    color: tier.highlighted ? T.blue : T.textDim,
                    background: tier.highlighted
                      ? `${T.blue}15`
                      : "transparent",
                    padding: tier.highlighted ? "4px 10px" : 0,
                    borderRadius: 6,
                  }}
                >
                  {tier.tag}
                </span>
              </div>
              <h3
                style={{
                  fontFamily: font,
                  fontWeight: 800,
                  fontSize: 26,
                  color: T.heading,
                  marginBottom: 8,
                  letterSpacing: "-0.02em",
                }}
              >
                {tier.name}
              </h3>
              <div style={{ marginBottom: 16 }}>
                <span
                  style={{
                    fontFamily: font,
                    fontWeight: 900,
                    fontSize: 36,
                    color: T.heading,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {tier.price}
                </span>
                {tier.price === "0" && (
                  <span
                    style={{
                      fontSize: 16,
                      color: T.textMuted,
                      marginLeft: 4,
                    }}
                  >
                    lei
                  </span>
                )}
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: T.textMuted,
                  lineHeight: 1.6,
                  marginBottom: 28,
                }}
              >
                {tier.desc}
              </p>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  marginBottom: 32,
                }}
              >
                {tier.features.map((f) => (
                  <div
                    key={f}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <Check
                      size={15}
                      color={tier.highlighted ? T.blueBright : T.textDim}
                      strokeWidth={2.5}
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <span
                      style={{
                        fontSize: 14,
                        color: T.text,
                        lineHeight: 1.4,
                      }}
                    >
                      {f}
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (tier.price === "0") {
                    onNavigate("/identificare-raportare");
                  }
                }}
                style={{
                  ...s.btn,
                  width: "100%",
                  justifyContent: "center",
                  borderRadius: 10,
                  padding: "14px 0",
                  fontSize: 14,
                  ...(tier.highlighted ? s.btnPrimary : s.btnSecondary),
                  boxShadow: tier.highlighted
                    ? `0 4px 20px ${T.blue}30`
                    : "none",
                }}
              >
                {tier.cta} <ArrowRight size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const items = [
    {
      q: "NIS2PASS înlocuiește platforma oficială NIS2@RO?",
      a: "Nu. NIS2PASS este un instrument auxiliar de conformare care automatizează pregătirea documentelor necesare. Platforma statului (NIS2@RO) rămâne canalul oficial de înregistrare și comunicare cu DNSC.",
    },
    {
      q: "Ce cadru legislativ acoperă NIS2PASS?",
      a: "NIS2PASS este aliniat cu Directiva (UE) 2022/2555, transpusă prin OUG nr. 155/2024 și Legea nr. 124/2025, precum și cu ordinele DNSC (nr. 1/2025, nr. 2/2025, nr. 4/2025) pentru evaluare și conformitate.",
    },
    {
      q: "Cât durează procesul de evaluare?",
      a: "Evaluarea inițială se completează rapid. Introdu CUI-ul companiei, iar NIS2PASS preia automat datele din bazele publice, determină încadrarea NIS2 și generează formularul de notificare DNSC.",
    },
    {
      q: "Ce se întâmplă dacă nu mă înregistrez la DNSC?",
      a: "Conform OUG 155/2024, nerespectarea obligației de înregistrare poate atrage amenzi de până la 500.000 lei pentru entitățile esențiale și până la 300.000 lei pentru entitățile importante. Termenul de înregistrare este de 30 de zile de la notificarea DNSC.",
    },
    {
      q: "Ce metodologie folosiți pentru evaluarea de maturitate?",
      a: "Folosim cadrul CyberFundamentals (CyFun®), conform Ordinului DNSC nr. 4/2025, care acoperă 217 măsuri de securitate evaluate pe 3 niveluri de maturitate: de bază, important și esențial.",
    },
  ];

  return (
    <section
      id="faq"
      className="landing-section-large-pad"
      style={{ padding: "140px 0", position: "relative" }}
    >
      <div style={{ ...s.container, maxWidth: 780 }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div style={{ ...s.eyebrow, justifyContent: "center" }}>
            <div style={{ width: 20, height: 1, background: T.blueBright }} />
            Întrebări frecvente
          </div>
          <h2
            style={{
              ...s.heading,
              fontSize: "clamp(32px, 4vw, 44px)",
            }}
          >
            Ai întrebări? <span style={s.gradient}>Avem răspunsuri.</span>
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((item, i) => (
            <div
              key={item.q}
              style={{
                ...s.card,
                overflow: "hidden",
                border:
                  open === i
                    ? `1px solid ${T.blueLight}30`
                    : `1px solid ${T.border}`,
                background: open === i ? `${T.blue}06` : T.bgCard,
              }}
            >
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "20px 24px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left" as const,
                }}
              >
                <span
                  style={{
                    fontFamily: font,
                    fontWeight: 700,
                    fontSize: 15,
                    color: open === i ? T.white : T.text,
                    transition: "color 0.2s",
                    lineHeight: 1.4,
                    paddingRight: 16,
                  }}
                >
                  {item.q}
                </span>
                <ChevronDown
                  size={18}
                  color={T.textDim}
                  style={{
                    transform: open === i ? "rotate(180deg)" : "none",
                    transition: "transform 0.3s ease",
                    flexShrink: 0,
                  }}
                />
              </button>
              <div
                style={{
                  maxHeight: open === i ? 500 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.4s ease, padding 0.3s ease",
                  padding: open === i ? "0 24px 20px" : "0 24px",
                }}
              >
                <p
                  style={{
                    fontSize: 14,
                    color: T.textMuted,
                    lineHeight: 1.75,
                  }}
                >
                  {item.a}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTABanner({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      style={{
        padding: "100px 0",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(135deg, ${T.blue}15, ${T.bgAlt}, ${T.blue}08)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.04,
          backgroundImage: `radial-gradient(${T.blueBright} 1px, transparent 1px)`,
          backgroundSize: "24px 24px",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${T.blue}15, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "10%",
          right: "10%",
          height: 1,
          background: `linear-gradient(90deg, transparent, ${T.blue}40, transparent)`,
        }}
      />

      <div
        style={{
          ...s.container,
          position: "relative",
          textAlign: "center",
          opacity: inView ? 1 : 0,
          transform: inView ? "none" : "translateY(24px)",
          transition: "all 0.7s ease",
        }}
      >
        <h2
          style={{
            ...s.heading,
            fontSize: "clamp(32px, 4.5vw, 48px)",
            marginBottom: 16,
          }}
        >
          Ai nevoie de ajutor pentru <br />
          <span style={s.gradient}>conformitatea NIS2?</span>
        </h2>
        <p
          style={{
            fontSize: 17,
            color: T.textMuted,
            maxWidth: 520,
            margin: "0 auto 12px",
            lineHeight: 1.65,
          }}
        >
          Începe cu evaluarea gratuită sau contactează echipa noastră de
          consultanți pentru suport personalizat.
        </p>
        <p
          style={{
            fontSize: 14,
            color: T.amber,
            fontWeight: 600,
            marginBottom: 36,
          }}
        >
          <AlertTriangle
            size={14}
            style={{ verticalAlign: "middle", marginRight: 6 }}
          />
          Amenzi de până la 500.000 lei pentru neconformare
        </p>
        <div
          className="landing-cta-buttons"
          style={{ display: "flex", justifyContent: "center", gap: 16 }}
        >
          <button
            type="button"
            onClick={() => onNavigate("/identificare-raportare")}
            style={{
              ...s.btn,
              ...s.btnPrimary,
              padding: "16px 36px",
              fontSize: 16,
              boxShadow: `0 4px 24px ${T.blue}40`,
              animation: "glowPulse 3s ease infinite",
            }}
          >
            Începe Evaluarea Gratuită <ArrowRight size={18} />
          </button>
          <a
            href="mailto:contact@nis2pass.ro"
            style={{
              ...s.btn,
              ...s.btnSecondary,
              padding: "16px 36px",
              fontSize: 16,
            }}
          >
            <Mail size={18} /> Contactează-ne
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      style={{
        borderTop: `1px solid ${T.border}`,
        padding: "48px 0 32px",
        background: T.bg,
      }}
    >
      <div style={{ ...s.container }}>
        <div
          className="landing-footer-cols"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 48,
            marginBottom: 40,
          }}
        >
          <div style={{ maxWidth: 300 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: `linear-gradient(135deg, ${T.blue}, ${T.blueBright})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={15} color={T.white} />
              </div>
              <span
                style={{
                  fontFamily: font,
                  fontWeight: 800,
                  fontSize: 16,
                  color: T.white,
                }}
              >
                NIS2PASS
              </span>
            </div>
            <p
              style={{
                fontSize: 13,
                color: T.textDim,
                lineHeight: 1.7,
                marginBottom: 16,
              }}
            >
              Platformă de conformitate NIS2 pentru România. Aliniat cu OUG
              155/2024, Legea 124/2025 și ordinele DNSC.
            </p>
          </div>
          <div
            className="landing-footer-links"
            style={{ display: "flex", gap: 64 }}
          >
            <div>
              <h4
                style={{
                  fontFamily: font,
                  fontWeight: 700,
                  fontSize: 13,
                  color: T.textMuted,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.08em",
                  marginBottom: 16,
                }}
              >
                Platformă
              </h4>
              {[
                "Evaluare NIS2",
                "Analiză de Risc",
                "Evaluare Maturitate",
                "Formular DNSC",
              ].map((link) => (
                <a
                  key={link}
                  href="#hero"
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: T.textDim,
                    textDecoration: "none",
                    marginBottom: 10,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.color = T.text;
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.color = T.textDim;
                  }}
                >
                  {link}
                </a>
              ))}
            </div>
            <div>
              <h4
                style={{
                  fontFamily: font,
                  fontWeight: 700,
                  fontSize: 13,
                  color: T.textMuted,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.08em",
                  marginBottom: 16,
                }}
              >
                Legal
              </h4>
              {[
                {
                  text: "Termeni și condiții",
                  href: "/assets/TERMENI_ȘI_CONDIȚII_GENERALE.pdf",
                },
                {
                  text: "Politica de confidențialitate",
                  href: "/assets/POLITICA_DE_CONFIDENȚIALITATE.pdf",
                },
              ].map((link) => (
                <a
                  key={link.text}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: T.textDim,
                    textDecoration: "none",
                    marginBottom: 10,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.color = T.text;
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.color = T.textDim;
                  }}
                >
                  {link.text}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div
          className="landing-footer-bottom"
          style={{
            borderTop: `1px solid ${T.border}`,
            paddingTop: 24,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: T.textDim }}>
            © 2026 NIS2PASS. Toate drepturile rezervate.
          </span>
          <span style={{ fontSize: 12, color: T.textDim }}>
            Alpha Version · Aplicație pentru evaluarea conformității cu OUG
            155/2024
          </span>
        </div>
      </div>
    </footer>
  );
}

export default function LandingAlternative() {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (path: string) => navigate(path),
    [navigate]
  );

  return (
    <div
      style={{
        fontFamily: font,
        color: T.text,
        background: T.bg,
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <FontLoader />
      <AnnouncementBar />
      <Nav onNavigate={handleNavigate} />
      <Hero onNavigate={handleNavigate} />
      <NewsCarousel />
      <PlatformCapabilities />
      <SemicirclesSection />
      <LegalFramework />
      <SocialProof />
      <Pricing onNavigate={handleNavigate} />
      <FAQ />
      <CTABanner onNavigate={handleNavigate} />
      <Footer />
    </div>
  );
}
